import React from 'react';
import {useParams} from 'react-router-dom';
import type {ProjectSimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {SidebarLinkProjectsAdmin} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {ProjectTable} from 'src/app/projects/ProjectTable/ProjectTable';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {RegionTable} from 'src/pages/Modules/Project/shared/RegionTable';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {ProjectIndexViewType} from 'src/shared/constants/ViewTypes';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const ProjectListPage: React.FC = () => {
  useSetDocumentTitle('Projects');
  const navigate = useAppNavigate();
  const {view} = useParams();

  const {hasPermission: hasCreatePermission} = useHasModulePermission({
    module: ModuleEnum.Projects,
    operation: PermissionOperationEnum.Create,
  });

  const handleNavigateToProject = (obj: ProjectSimpleResponse) => {
    navigate(PathUtility.ProjectView(obj.id));
  };

  const handleAddNew = () => navigate(PathUtility.ProjectCreate());

  const {RenderedTabsComponent} = useUrlQueryTabV2<ProjectIndexViewType>({
    view: (view ?? '') as ProjectIndexViewType,
    defaultView: 'list',
    tabs: [
      {
        view: 'list',
        label: 'Projects',
        component: (
          <ProjectTable
            identifier="project-list-page"
            onViewRowClick={handleNavigateToProject}
            onCreate={hasCreatePermission ? handleAddNew : undefined}
          />
        ),
      },
      {
        view: 'regions',
        label: 'Regions',
        component: <RegionTable identifier="region-list-page" />,
      },
    ],
    onSelectView: nextView =>
      navigate(PathUtility.Project(nextView as ProjectIndexViewType)),
  });

  return (
    <ContentWrapper>
      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkProjectsAdmin.icon}
          subtitle="Manage projects and data access">
          Projects
        </PageHeader.Title>
      </PageHeader>
      {RenderedTabsComponent}
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.Project(':view'),
  element: <ProjectListPage />,
  module: ModuleEnum.Projects,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
  allowGlobalAdminBypass: true,
});
